import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { Queue, useSearchElasticStats } from "@coherehealth/qm-api";
import { defaultSkill } from "../util/ChartUtil";
import { Body1, Card, H1 } from "@coherehealth/common";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Divider } from "@mui/material";
import {
  historicalQueueSkillsAggQueryGenerator,
  dueDateStatsQueueBasedQueryGenerator,
  AggregationData,
  filtersWithoutFaxAutoAttachFilter,
} from "../util/QueryUtil";
import config from "api/config";
import StatsTable from "./StatsTable";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    statsContainer: {
      padding: "24px 32px 0px",
    },
    skillSpecificStatsInnerContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 4,
      width: 180,
    },
    skillSpecificStatsOuterContainerHorizontal: {
      display: "flex",
      flexDirection: "row",
      gap: 24,
      padding: "9.5px 24px",
    },
    skillSpecificStatsOuterContainerVertical: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      padding: "0px 48px 7px 24px",
    },
    lightBody: {
      color: theme.palette.text.tertiary,
    },
  })
);

interface QueueVolumeHistoricalStatsContentProps {
  queue: Queue;
  setHasError: Dispatch<SetStateAction<boolean>>;
  skillValue: string;
  skills: (string | undefined)[] | undefined;
}

const QueueVolumeHistoricalStatsContent = ({
  queue,
  setHasError,
  skillValue,
  skills,
}: QueueVolumeHistoricalStatsContentProps) => {
  const classes = useStyles();
  const [currentQueueVolume, setCurrentQueueVolume] = useState<number>(0);
  const [currentQueueVolumeAgg, setCurrentQueueVolumeAgg] = useState<AggregationData>({});
  const [incomingQueueVolume, setIncomingQueueVolume] = useState<number>(0);
  const [incomingQueueVolumeAgg, setIncomingQueueVolumeAgg] = useState<AggregationData>({});
  const [productionVolume, setProductionVolume] = useState<number>(0);
  const [productionVolumeAgg, setProductionVolumeAgg] = useState<AggregationData>({});

  const searchSkill = skillValue === defaultSkill.id ? undefined : skillValue;
  const hasAggData = useMemo(() => {
    return !!(
      Object.values(currentQueueVolumeAgg || {})
        .concat(Object.values(incomingQueueVolumeAgg || {}))
        .concat(Object.values(productionVolumeAgg || {}))
        .reduce((acc, curr) => acc + (curr["count"] || 0), 0) && !searchSkill
    );
  }, [currentQueueVolumeAgg, incomingQueueVolumeAgg, productionVolumeAgg, searchSkill]);

  const { mutate: searchElasticStats, error } = useSearchElasticStats({
    base: `${config.QM_SERVICE_API_URL}`,
  });

  const currentQueueVolumePayload = useMemo(() => {
    const filters = filtersWithoutFaxAutoAttachFilter(queue.filters?.slice() || []);
    if (!!searchSkill) {
      filters?.push({
        attribute: "requiredSkills",
        filterValue: searchSkill,
        queryOperator: "EQ",
        logicalOperator: "AND",
      });
    }

    return dueDateStatsQueueBasedQueryGenerator(filters, [
      {
        fieldName: "requiredSkills.keyword",
        name: "requiredSkills",
      },
    ]);
  }, [queue.filters, searchSkill]);

  const incomingVolumePayload = useMemo(
    () =>
      historicalQueueSkillsAggQueryGenerator(
        "1",
        filtersWithoutFaxAutoAttachFilter(queue.filters || []),
        "dateCreatedAsLong",
        undefined,
        searchSkill
      ),
    [queue.filters, searchSkill]
  );

  const productionVolumePayload = useMemo(
    () =>
      historicalQueueSkillsAggQueryGenerator(
        "1",
        filtersWithoutFaxAutoAttachFilter(queue.filters || []),
        "outcome.dateCompletedAsLong",
        ["CANCELLED", "IN_PROGRESS", "OPEN"],
        searchSkill
      ),
    [queue.filters, searchSkill]
  );

  useEffect(() => {
    const fetchStatsData = async () => {
      const res = await searchElasticStats(currentQueueVolumePayload);
      setCurrentQueueVolume(res?.hits?.totalHits?.value || 0);
      setCurrentQueueVolumeAgg(res.aggregations?.requiredSkills);
    };

    fetchStatsData();
  }, [currentQueueVolumePayload, searchElasticStats]);

  useEffect(() => {
    const fetch = async () => {
      const res = await searchElasticStats(incomingVolumePayload);
      setIncomingQueueVolume(res?.hits?.totalHits?.value || 0);
      setIncomingQueueVolumeAgg(res.aggregations?.requiredSkills);
    };
    fetch();
  }, [incomingVolumePayload, searchElasticStats]);

  useEffect(() => {
    const fetch = async () => {
      const res = await searchElasticStats(productionVolumePayload);
      setProductionVolume(res?.hits?.totalHits?.value || 0);
      setProductionVolumeAgg(res.aggregations?.requiredSkills);
    };
    fetch();
  }, [productionVolumePayload, searchElasticStats]);

  useEffect(() => {
    if (error) {
      setHasError(true);
    }
  }, [error, setHasError]);

  return (
    <Card>
      {hasAggData ? (
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexDirection: "column" }}>
            <div>
              <Body1 style={{ padding: "8px 0px 32px 24px" }} className={classes.lightBody}>
                Total volume
              </Body1>
            </div>
            <div className={classes.skillSpecificStatsOuterContainerVertical}>
              <div className={classes.skillSpecificStatsInnerContainer}>
                <Body1 className={classes.lightBody}>Current queue volume</Body1>
                <H1>{currentQueueVolume}</H1>
              </div>
              <Divider orientation="horizontal" style={{ width: 180 }} />
              <div className={classes.skillSpecificStatsInnerContainer}>
                <Body1 className={classes.lightBody}>Incoming volume</Body1>
                <H1>{incomingQueueVolume}</H1>
              </div>
              <Divider orientation="horizontal" style={{ width: 180 }} />
              <div className={classes.skillSpecificStatsInnerContainer}>
                <Body1 className={classes.lightBody}>Production volume</Body1>
                <H1>{productionVolume}</H1>
              </div>
            </div>
          </div>
          <Divider orientation="vertical" style={{ height: 273 }} />
          <StatsTable
            skills={skills}
            data={{
              currentQueueVolume: currentQueueVolumeAgg,
              incomingVolume: incomingQueueVolumeAgg,
              productionVolume: productionVolumeAgg,
            }}
          />
        </div>
      ) : (
        <div className={classes.skillSpecificStatsOuterContainerHorizontal}>
          <div className={classes.skillSpecificStatsInnerContainer}>
            <Body1 className={classes.lightBody}>Current queue volume</Body1>
            <H1>{currentQueueVolume}</H1>
          </div>
          <Divider orientation="vertical" style={{ width: 5, height: 60 }} />
          <div className={classes.skillSpecificStatsInnerContainer}>
            <Body1 className={classes.lightBody}>Incoming volume</Body1>
            <H1>{incomingQueueVolume}</H1>
          </div>
          <Divider orientation="vertical" style={{ width: 5, height: 60 }} />
          <div className={classes.skillSpecificStatsInnerContainer}>
            <Body1 className={classes.lightBody}>Production volume</Body1>
            <H1>{productionVolume}</H1>
          </div>
        </div>
      )}
    </Card>
  );
};

export default QueueVolumeHistoricalStatsContent;
